// Hero-Slider Component
// Original slider implementation converted to React
// Now using JSON-based CMS for hero-slider management
// Supports images, videos, and GIFs with mobile-specific content

import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Hero.css';
import slidesData from '../data/hero-slides.json';
import { getHeroContent } from '../utils/contentLoader';

function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mediaErrors, setMediaErrors] = useState({});
  const sliderRef = useRef(null);
  const containerRef = useRef(null);
  const autoSlideTimeoutRef = useRef(null);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load slides from JSON - filter by enabled, then by viewport-specific rules
  const allEnabledSlides = slidesData.slides.filter(slide => slide.enabled);
  const slides = allEnabledSlides.filter(slide => {
    if (isMobile) {
      // On mobile: exclude slides marked as desktop-only (hideMediaOnMobile: true)
      return !slide.hideMediaOnMobile;
    } else {
      // On desktop: exclude slides marked as mobile-only
      return !slide.mobileOnly;
    }
  });
  
  const slideCount = slides.length;
  const numOfSlides = Math.max(0, slideCount - 1);
  const animTime = slidesData.settings.animationTime || 500;
  const autoSlideDelay = slidesData.settings.autoSlideDelay || 6000;

  // Reset to first slide when viewport changes and current slide becomes invalid
  useEffect(() => {
    if (currentSlide > numOfSlides && slides.length > 0) {
      setCurrentSlide(0);
    }
  }, [currentSlide, numOfSlides, slides.length]);

  const clearAutoSlide = useCallback(() => {
    if (autoSlideTimeoutRef.current) {
      clearTimeout(autoSlideTimeoutRef.current);
      autoSlideTimeoutRef.current = null;
    }
  }, []);

  const startAnimation = useCallback(() => {
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), animTime);
  }, [animTime]);

  const navigateLeft = () => {
    if (isAnimating || currentSlide === 0) return;
    startAnimation();
    clearAutoSlide();
    setCurrentSlide(prev => prev - 1);
  };

  const navigateRight = () => {
    if (isAnimating || currentSlide === numOfSlides) return;
    startAnimation();
    clearAutoSlide();
    setCurrentSlide(prev => prev + 1);
  };

  // Auto-slide timer: schedule next slide from current position
  useEffect(() => {
    if (slideCount <= 1) return () => {};
    clearAutoSlide();
    autoSlideTimeoutRef.current = setTimeout(() => {
      startAnimation();
      setCurrentSlide(prev => (prev + 1) % slideCount);
    }, autoSlideDelay);
    return () => {
      clearAutoSlide();
    };
  }, [autoSlideDelay, clearAutoSlide, currentSlide, slideCount, startAnimation]);

  // Click handler for slider - click left side to go left, right side to go right
  const handleSliderClick = (e) => {
    if (isAnimating) return;
    clearAutoSlide();
    
    const slider = sliderRef.current;
    if (!slider) return;
    
    const rect = slider.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const sliderWidth = rect.width;
    const middle = sliderWidth / 2;
    
    if (clickX < middle) {
      // Clicked left side - go to previous slide
      if (currentSlide > 0) {
        navigateLeft();
      }
    } else {
      // Clicked right side - go to next slide
      if (currentSlide < numOfSlides) {
        navigateRight();
      }
    }
  };

  // Scroll hijacking removed; slider uses click zones, edge controls, and auto-advance

  const sliderTransform = `translate3d(${-currentSlide * 100}%, 0, 0)`;

  const heroContent = getHeroContent();
  const ctaLabel = heroContent?.ctaLabel || 'Experience it';
  const ctaSectionId = heroContent?.ctaSectionId || 'contact';

  const handleHeroCtaClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const el = document.getElementById(ctaSectionId);
    el?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div id="hero-root" className="hero-slider-container" ref={containerRef}>
      {/* Centered tagline + CTA overlay */}
      <div className="hero-tagline-overlay">
        <div className="hero-tagline-stack">
          <p className="hero-tagline">
            {heroContent?.tagline || "Home is not a place, it's a feeling."}
          </p>
          <a
            href={`#${ctaSectionId}`}
            className="btn btn-primary hero-tagline-cta"
            onClick={handleHeroCtaClick}
          >
            {ctaLabel}
          </a>
        </div>
      </div>

      <div 
        className={`slider-control left ${currentSlide === 0 ? 'inactive' : ''}`}
        onClick={navigateLeft}
      ></div>
      <div 
        className={`slider-control right ${currentSlide === numOfSlides ? 'inactive' : ''}`}
        onClick={navigateRight}
      ></div>

      <div 
        className={`hero-slider ${isAnimating ? 'animating' : ''}`}
        ref={sliderRef}
        style={{ transform: sliderTransform }}
        onClick={handleSliderClick}
      >
        {slides.map((slide, index) => {
          // Build slide style
          const slideStyle = {
            backgroundColor: slide.bgColor
          };
          
          // Handle media loading error
          const handleMediaError = () => {
            setMediaErrors(prev => ({ ...prev, [slide.id]: true }));
          };
          
          // Render media based on type
          const renderMedia = () => {
            if (!slide.mediaFile || mediaErrors[slide.id]) return null;
            
            try {
              const mediaModule = require(`../assets/hero-slides/${slide.mediaFile}`);
              const mediaPath = mediaModule.default || mediaModule;
              
              switch(slide.mediaType) {
                case 'video':
                  return (
                    <video 
                      className="hero-slider-media hero-slider-media--video"
                      autoPlay 
                      loop 
                      muted 
                      playsInline
                      onError={handleMediaError}
                    >
                      <source src={mediaPath} type="video/mp4" />
                      <source src={mediaPath} type="video/webm" />
                    </video>
                  );
                
                case 'gif':
                case 'image':
                default:
                  return (
                    <img
                      src={mediaPath}
                      alt={slide.title || 'hero slide'}
                      loading="lazy"
                      className="hero-slider-media hero-slider-media--image"
                      onError={handleMediaError}
                    />
                  );
              }
            } catch (error) {
              handleMediaError();
              return null;
            }
          };
          
          const mediaContent = renderMedia();
          const hasMedia = Boolean(mediaContent);
          
          return (
            <div 
              key={slide.id}
              className={`hero-slider-slide slide-${index} ${index === currentSlide ? 'active' : ''} ${!hasMedia ? 'no-media' : ''}`}
              style={slideStyle}
            >
              {hasMedia && (
                <div className="hero-slider-media-frame">
                  {mediaContent}
                </div>
              )}
              {/* Only show title and description when there's no media or media failed to load */}
              {!hasMedia && (
                <div className="hero-slider-slide__content">
                  <div className="pillar-content">
                    <h2 className="pillar-title-large">{slide.title}</h2>
                    <p className="pillar-desc-large">{slide.description}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Hero;
